import { useMemo, useState } from 'react';
import { calculateROI } from '../services/roiService';
import { CalculatorFormState, ROIResults } from '../types';

const defaultForm: CalculatorFormState = {
    monthlyLeads: 120,
    bookingRate: 40,
    showRate: 75,
    closeRate: 30,
    averageOrderValue: 150,
    retentionRate: 20,
    retentionMonths: 3,
    marketingSpend: 2500,
    fixedCosts: 800,
};

const createDefaultForm = (): CalculatorFormState => ({ ...defaultForm });

const isPercentageField = (field: keyof CalculatorFormState): boolean =>
    field === 'bookingRate' ||
    field === 'showRate' ||
    field === 'closeRate' ||
    field === 'retentionRate';

const ensureSanitizedValue = (field: keyof CalculatorFormState, value: number): number => {
    const numericValue = Number.isNaN(value) ? 0 : value;
    if (isPercentageField(field)) {
        return Math.min(Math.max(numericValue, 0), 100);
    }
    if (field === 'retentionMonths') {
        return Math.max(numericValue, 0);
    }
    if (field === 'monthlyLeads') {
        return Math.max(Math.round(numericValue), 0);
    }
    return Math.max(numericValue, 0);
};

const useCalculator = () => {
    const [formValues, setFormValues] = useState<CalculatorFormState>(createDefaultForm);
    const [results, setResults] = useState<ROIResults | null>(null);

    const updateField = (field: keyof CalculatorFormState, value: number) => {
        setFormValues((prev) => ({
            ...prev,
            [field]: ensureSanitizedValue(field, value),
        }));
    };

    const bulkUpdate = (updates: Partial<CalculatorFormState>) => {
        setFormValues((prev) => ({
            ...prev,
            ...Object.entries(updates).reduce<Partial<CalculatorFormState>>((acc, [key, val]) => {
                const typedKey = key as keyof CalculatorFormState;
                if (typeof val === 'number') {
                    acc[typedKey] = ensureSanitizedValue(typedKey, val);
                }
                return acc;
            }, {}),
        }));
    };

    const resetCalculator = () => {
        setFormValues(createDefaultForm());
        setResults(null);
    };

    const calculate = () => {
        const output = calculateROI(formValues);
        setResults(output);
        return output;
    };

    const hasResults = useMemo(() => results !== null, [results]);

    return {
        formValues,
        updateField,
        bulkUpdate,
        resetCalculator,
        calculate,
        results,
        hasResults,
    };
};

export default useCalculator;
export { useCalculator };
